import {Component, inject, OnInit} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {
    IConfiguredFunctionRequestPolicy,
    IFormSubmissionFunctionBinding,
    IFunctionArgumentLookup,
    IHubSpotFormPresentation,
    IFunctionBearerCredentialUpdateRequest,
    IFunctionOptions,
    IWorkflowFunctionBinding,
    isWorkflowFunctionBindingReady,
    workflowFunctionBindingUnavailableMessage
} from '../../../../models/function-options.model';
import {MaterialModule} from '../../../../material.module';
import {DatePipe} from '@angular/common';
import {ToastService} from '../../../../services/layout/toast.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import {FunctionGeneralSettingsComponent} from '../function-general-settings/function-general-settings.component';
import {FunctionsService} from "../../../../services/objects/functions.service";
import {TabbedPageComponent} from "../../../objects/tabbed-page/tabbed-page.component";
import {ClientObjectSettings} from "../../../objects/client-object-settings/client-object-settings.component";
import {FunctionTestComponent} from "../function-test/function-test.component";
import {map, Observable, of, switchMap} from 'rxjs';

@Component({
  selector: 'app-function-settings',
  templateUrl: './function-settings.component.html',
  styleUrls: ['./function-settings.component.scss'],
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    FunctionGeneralSettingsComponent,
    DatePipe,
    ClientObjectSettings,
    FunctionTestComponent
  ],
  standalone: true
})
export class FunctionSettingsComponent extends TabbedPageComponent implements OnInit {
    private fb = inject(FormBuilder);
    private functionService = inject(FunctionsService);
    private snackBar = inject(ToastService);
    private dialog = inject(MatDialog);


  isSaving: boolean = false;
  isDeleting: boolean = false;

  functionOption: IFunctionOptions | null = null;
  id: string = '';
  workflowBinding: IWorkflowFunctionBinding | null = null;
  formSubmissionTargets: IFunctionOptions[] = [];

  // Reactive Forms
  settingsForm: FormGroup;
  ngOnInit(): void {
    super.OnInit()

    this.settingsForm = this.fb.group({
        parameters: [JSON.stringify({}, null, 2), [Validators.required, this.jsonValidator]],
      type: ['Logic App', Validators.required],
      endpoint: ['', this.getEndpointValidators('Logic App')],
        bearerKey: [''],
      contractVersion: ['configured-function.v1'],
      outputSchema: ['{"type":"object","properties":{},"additionalProperties":false}', [Validators.required, this.closedSchemaValidator]],
      authority: ['configured_provider', Validators.required],
      timeoutMs: [15000, [Validators.required, Validators.min(1000), Validators.max(60000)]],
      maximumAttempts: [1, [Validators.required, Validators.min(1), Validators.max(3)]],
      initialBackoffMs: [250, [Validators.required, Validators.min(0)]],
      maximumConcurrency: [1, [Validators.required, Validators.min(1)]],
      requestMethod: ['POST', [Validators.required, Validators.pattern(/^POST$/)]],
      requestArgumentPlacement: ['json_body', [Validators.required, Validators.pattern(/^json_body$/)]],
      idempotencyMode: ['provider_call_id', Validators.required],
      idempotencyHeaderName: ['Idempotency-Key'],
      argumentLookupsJson: ['[]'],
      assistantMediaHosts: ['', this.assistantMediaHostsValidator],
      waitMessage: [''],
      emailRecipient: [''],
      emailSubject: [''],
      formSubmissionEnabled: [false],
      formSubmissionFunctionId: [''],
      formSubmissionFunctionVersion: [1],
      formSubmissionToolName: [''],
      formSubmissionTargetContractVersion: [
        'configured-function.v1',
        Validators.pattern(/^configured-function\.v1$/)
      ],
      formSubmissionInputSchema: [''],
      hubSpotFormRegion: ['eu1'],
      hubSpotFormPortalId: [''],
      hubSpotFormId: ['']
    });

    this.updateTypeValidators('Logic App');
    this.updateContractValidators(true);
    this.updateFormSubmissionValidators();
    this.settingsForm.get('type')?.valueChanges.subscribe(type => {
      this.updateTypeValidators(type);
      this.updateContractValidators(this.settingsForm.get('contractVersion')?.value === 'configured-function.v1');
      this.updateFormSubmissionValidators();
    });
    this.settingsForm.get('formSubmissionEnabled')?.valueChanges.subscribe(() =>
      this.updateFormSubmissionValidators());
    this.settingsForm.get('idempotencyMode')?.valueChanges.subscribe(() => {
      this.updateIdempotencyValidators(
        this.settingsForm.get('contractVersion')?.value === 'configured-function.v1');
    });

    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.loadData();
    });
  }

    private readonly jsonValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (value === null || value === undefined || value === '') {
            return null; // Required validator handles empty states
        }

        if (typeof value !== 'string') {
            return {invalidJson: true};
        }

        try {
            const schema = JSON.parse(value);
            return this.findMissingRequiredProperty(schema) === null
              ? null
              : {requiredPropertyMissing: true};
        } catch {
            return {invalidJson: true};
        }
    };

    private findMissingRequiredProperty(schema: unknown): string | null {
      if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return null;
      const node = schema as Record<string, unknown>;
      const properties = node['properties'] && typeof node['properties'] === 'object' &&
        !Array.isArray(node['properties'])
        ? node['properties'] as Record<string, unknown>
        : {};
      if (Array.isArray(node['required'])) {
        const missing = node['required'].find(name =>
          typeof name === 'string' && name.trim().length > 0 && !Object.prototype.hasOwnProperty.call(properties, name));
        if (typeof missing === 'string') return missing;
      }
      for (const propertySchema of Object.values(properties)) {
        const missing = this.findMissingRequiredProperty(propertySchema);
        if (missing !== null) return missing;
      }
      const itemsMissing = this.findMissingRequiredProperty(node['items']);
      if (itemsMissing !== null) return itemsMissing;
      if (Array.isArray(node['anyOf'])) {
        for (const alternative of node['anyOf']) {
          const missing = this.findMissingRequiredProperty(alternative);
          if (missing !== null) return missing;
        }
      }
      for (const definitionsName of ['$defs', 'definitions']) {
        const definitions = node[definitionsName];
        if (!definitions || typeof definitions !== 'object' || Array.isArray(definitions)) continue;
        for (const definition of Object.values(definitions as Record<string, unknown>)) {
          const missing = this.findMissingRequiredProperty(definition);
          if (missing !== null) return missing;
        }
      }
      return null;
    }

    private readonly assistantMediaHostsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const lines = this.parseAssistantMediaHosts(control.value);
      if (lines.length > 20) return {tooManyHosts: true};
      if (new Set(lines).size !== lines.length) return {duplicateHost: true};
      return lines.every(host => this.isExactPublicDnsHost(host)) ? null : {invalidHost: true};
    };

  async loadData() {
    try {
      // Fetch Function Data
      const functionResponse = await this.functionService.getByIdAsync(this.id).toPromise();
      this.functionOption = functionResponse as IFunctionOptions;
      const isGoverned = this.functionOption?.contractVersion === 'configured-function.v1';
      const formBinding = this.functionOption?.formSubmissionBinding;
      const presentation = this.functionOption?.hubSpotFormPresentation;

      // Populate Settings Form
      this.settingsForm.patchValue({
        parameters: this.functionOption?.parameters,
        type: this.functionOption?.type,
        endpoint: this.functionOption?.endpoint,
        bearerKey: this.functionOption?.bearerKey,
        contractVersion: isGoverned ? this.functionOption.contractVersion : '',
        outputSchema: isGoverned ? this.functionOption.outputSchema : '',
        authority: isGoverned ? this.functionOption.authority : '',
        timeoutMs: this.functionOption?.execution?.timeoutMs ?? 15000,
        maximumAttempts: this.functionOption?.execution?.maximumAttempts ?? 1,
        initialBackoffMs: this.functionOption?.execution?.initialBackoffMs ?? 250,
        maximumConcurrency: this.functionOption?.execution?.maximumConcurrency ?? 1,
        requestMethod: this.functionOption?.request?.method ?? 'POST',
        requestArgumentPlacement: this.functionOption?.request?.argumentPlacement ?? 'json_body',
        idempotencyMode: this.functionOption?.idempotency?.mode ??
          (isGoverned && this.functionOption?.type === 'Logic App' ? 'provider_call_id' : 'none'),
        idempotencyHeaderName: this.functionOption?.idempotency?.headerName ??
          (isGoverned && this.functionOption?.type === 'Logic App' ? 'Idempotency-Key' : ''),
        argumentLookupsJson: JSON.stringify(this.functionOption?.argumentLookups ?? [], null, 2),
        assistantMediaHosts: (this.functionOption?.assistantMediaHostAllowlist ?? []).join('\n'),
        waitMessage: this.functionOption?.waitMessage,
        emailRecipient: this.functionOption?.emailRecipients?.[0]?.email ?? '',
        emailSubject: this.functionOption?.emailSubjectTemplate ?? '',
        formSubmissionEnabled: formBinding !== null && formBinding !== undefined,
        formSubmissionFunctionId: formBinding?.functionId ?? '',
        formSubmissionFunctionVersion: formBinding?.functionVersion ?? 1,
        formSubmissionToolName: formBinding?.toolName ?? '',
        formSubmissionTargetContractVersion: formBinding?.contractVersion ?? 'configured-function.v1',
        formSubmissionInputSchema: formBinding?.inputSchema ?? '',
        hubSpotFormRegion: presentation?.region ?? 'eu1',
        hubSpotFormPortalId: presentation?.portalId ?? '',
        hubSpotFormId: presentation?.formId ?? ''
      });
      this.updateContractValidators(isGoverned);
      this.updateFormSubmissionValidators();
      this.workflowBinding = this.functionOption?.workflowBinding ?? null;
      if (this.functionOption?.type === 'UI') {
        await this.loadFormSubmissionTargets();
      }

    } catch (error) {
      console.error('Error loading data', error);
      this.snackBar.showError('Error loading data', 3000);
    }
  }


  save() {
    if (!this.functionOption) return;
    if (this.requiresWorkflowBinding() && !this.hasReadyWorkflowBinding()) {
      this.snackBar.showError(workflowFunctionBindingUnavailableMessage(this.workflowBinding), 3000);
      return;
    }
    if (this.settingsForm.invalid) {
      this.snackBar.showError('Please fix the errors in the form before saving.', 3000);
      return;
    }

    this.isSaving = true;

    const preimage = JSON.parse(JSON.stringify(this.functionOption)) as IFunctionOptions;
    const isGoverned = preimage.contractVersion === 'configured-function.v1';
    const {
      emailRecipient,
      emailSubject,
      assistantMediaHosts,
      timeoutMs,
      maximumAttempts,
      initialBackoffMs,
      maximumConcurrency,
      requestMethod,
      requestArgumentPlacement,
      idempotencyMode,
      idempotencyHeaderName,
      argumentLookupsJson,
      formSubmissionEnabled,
      formSubmissionFunctionId,
      formSubmissionFunctionVersion,
      formSubmissionToolName,
      formSubmissionTargetContractVersion,
      formSubmissionInputSchema,
      hubSpotFormRegion,
      hubSpotFormPortalId,
      hubSpotFormId,
      ...settingsValues
    } = this.settingsForm.getRawValue();
    const loadedEndpoint = this.functionOption.endpoint;
    const endpointHasCredentialQuery = this.functionOption.endpointCredentialConfigured === true;
    Object.assign(this.functionOption, settingsValues);
    if (isGoverned) {
      this.functionOption.execution = {timeoutMs, maximumAttempts, initialBackoffMs, maximumConcurrency};
      if (settingsValues.type === 'Logic App' &&
          (preimage.request !== undefined ||
           this.settingsForm.get('requestMethod')?.dirty ||
           this.settingsForm.get('requestArgumentPlacement')?.dirty)) {
        this.functionOption.request = {
          method: requestMethod,
          argumentPlacement: requestArgumentPlacement
        } as IConfiguredFunctionRequestPolicy;
      } else {
        this.functionOption.request = preimage.request;
      }
      this.functionOption.idempotency = {mode: idempotencyMode, headerName: idempotencyMode === 'none' ? undefined : idempotencyHeaderName.trim()};
    } else {
      // A normal edit must never relabel or partially migrate a legacy function.
      this.functionOption.contractVersion = preimage.contractVersion;
      this.functionOption.outputSchema = preimage.outputSchema;
      this.functionOption.authority = preimage.authority;
      this.functionOption.execution = preimage.execution;
      this.functionOption.request = preimage.request;
      this.functionOption.idempotency = preimage.idempotency;
    }
    this.functionOption.argumentLookups = settingsValues.type === 'Logic App' &&
      (preimage.argumentLookups !== undefined || this.settingsForm.get('argumentLookupsJson')?.dirty)
      ? this.parseArgumentLookups(argumentLookupsJson)
      : preimage.argumentLookups;
    this.functionOption.assistantMediaHostAllowlist = this.parseAssistantMediaHosts(assistantMediaHosts);
    if (this.functionOption.type === 'Email') {
      this.functionOption.parameters = '{}';
      this.functionOption.endpoint = '';
      this.functionOption.bearerKey = '';
      this.functionOption.assistantMediaHostAllowlist = [];
      this.functionOption.emailRecipients = [{
        key: 'fixed',
        label: 'Configured recipient',
        email: emailRecipient.trim(),
        description: ''
      }];
      this.functionOption.emailSubjectTemplate = emailSubject.trim();
      this.functionOption.emailBodyTemplate = '';
      this.functionOption.emailRequireConsentToContact = false;
      this.functionOption.emailIncludeTranscript = false;
    }
    this.functionOption.workflowBinding = this.workflowBinding;
    if (this.functionOption.type === 'Workflow' && this.workflowBinding) {
      this.functionOption.parameters = this.workflowBinding.inputSchema;
    }
    const savePayload: IFunctionOptions = {
      ...this.functionOption,
      workflowBinding: this.workflowBindingForSave(),
      formSubmissionBinding: this.formSubmissionBindingForSave({
        enabled: formSubmissionEnabled,
        functionId: formSubmissionFunctionId,
        functionVersion: formSubmissionFunctionVersion,
        toolName: formSubmissionToolName,
        contractVersion: formSubmissionTargetContractVersion,
        inputSchema: formSubmissionInputSchema
      }, preimage.formSubmissionBinding),
      hubSpotFormPresentation: this.hubSpotFormPresentationForSave({
        enabled: formSubmissionEnabled,
        region: hubSpotFormRegion,
        portalId: hubSpotFormPortalId,
        formId: hubSpotFormId
      }, preimage.hubSpotFormPresentation)
    };
    if (endpointHasCredentialQuery && settingsValues.endpoint === loadedEndpoint) {
      savePayload.endpoint = '[unchanged-endpoint]';
    }
    savePayload.bearerKey = savePayload.bearerKey ?? preimage.bearerKey ?? '';

    let generalChanges = this.diffPatchChanges(preimage, savePayload);
    const mediaHostsChanged = !this.valuesEqual(
      preimage.assistantMediaHostAllowlist ?? [],
      savePayload.assistantMediaHostAllowlist ?? []
    );
    const bearerCredentialChanged = (preimage.bearerKey ?? '') !== (savePayload.bearerKey ?? '');
    if (bearerCredentialChanged) {
      // Endpoint + credential rotation is one conditional operation; never expose a
      // transient configuration with a new endpoint and the previous credential.
      generalChanges = generalChanges.filter(change => change.field !== 'endpoint');
    }
    if (this.functionOption.etag && generalChanges.length === 0 && !mediaHostsChanged && !bearerCredentialChanged) {
      this.snackBar.showInfo('No configuration changes to save.', 3000);
      this.isSaving = false;
      return;
    }

    // Existing media-host allowlists have a dedicated one-field conditional-write boundary.
    const saveRequest = this.functionOption.etag
      ? this.saveExistingFunction(preimage, savePayload, generalChanges, mediaHostsChanged, bearerCredentialChanged)
      : this.functionService.saveAsync(savePayload);
    saveRequest.subscribe((saved: IFunctionOptions) => {
      this.functionOption = saved || this.functionOption;
      this.snackBar.showSuccess('Settings saved successfully', 3000);
      this.isSaving = false;
    }, error => {
      console.error('Error saving settings', {
        status: error?.status,
        outcome: error?.error?.outcome ?? 'unknown'
      });
      const message = error?.status === 412
        ? 'Configuration changed elsewhere. Reload and compare before saving.'
        : error?.error?.outcome === 'outcome_unknown'
          ? 'Save outcome unknown. Reload the function before retrying; the recovery reference was retained.'
          : error?.error?.outcome === 'not_committed'
            ? 'Configuration was not saved. Retry using the retained recovery reference.'
            : 'Error saving settings';
      this.snackBar.showError(message, 5000);
      this.isSaving = false;
    });
  }

  private readonly closedSchemaValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    try {
      const schema = JSON.parse(control.value);
      return schema?.type === 'object' && schema?.additionalProperties === false && schema?.properties && typeof schema.properties === 'object'
        ? null : {closedSchema: true};
    } catch { return {closedSchema: true}; }
  };

  private diffPatchChanges(current: IFunctionOptions, next: IFunctionOptions): {field: string; oldValue: unknown; newValue: unknown}[] {
    const fields: (keyof IFunctionOptions)[] = [
      'name', 'description', 'enabled', 'parameters', 'outputSchema', 'authority', 'execution', 'request', 'idempotency',
      'type', 'endpoint', 'waitMessage', 'contractVersion', 'emailRecipients', 'emailSubjectTemplate',
      'emailBodyTemplate', 'emailRequireConsentToContact', 'emailIncludeTranscript', 'workflowBinding',
      'formSubmissionBinding', 'hubSpotFormPresentation', 'argumentLookups'
    ];
    return fields.filter(field => JSON.stringify(current[field]) !== JSON.stringify(next[field]))
      .map(field => ({field: String(field), oldValue: current[field], newValue: next[field]}));
  }

  private saveExistingFunction(
    preimage: IFunctionOptions,
    next: IFunctionOptions,
    generalChanges: {field: string; oldValue: unknown; newValue: unknown}[],
    mediaHostsChanged: boolean,
    bearerCredentialChanged: boolean
  ): Observable<IFunctionOptions> {
    const currentEtag = preimage.etag!;
    const generalSave = generalChanges.length > 0
      ? this.functionService.patchAsync(preimage.id, {etag: currentEtag, changes: generalChanges})
      : of(preimage);

    const mediaSave = generalSave.pipe(switchMap(saved => {
      if (!mediaHostsChanged || next.type !== 'Logic App') return of(saved);
      if (!saved.etag) throw new Error('The function save response did not include an ETag.');

      return this.functionService.patchAssistantMediaHostAllowlistAsync(preimage.id, {
        etag: saved.etag,
        assistantMediaHostAllowlist: next.assistantMediaHostAllowlist ?? []
      }).pipe(map(update => ({
        ...saved,
        etag: update.etag,
        assistantMediaHostAllowlist: update.assistantMediaHostAllowlist,
        updatedDate: update.updatedDate
      })));
    }));

    return mediaSave.pipe(switchMap(saved => {
      if (!bearerCredentialChanged || next.type !== 'Logic App') return of(saved);
      if (!saved.etag) throw new Error('The function save response did not include an ETag.');
      const bearerKey = next.bearerKey?.trim() ?? '';
      const request: IFunctionBearerCredentialUpdateRequest = bearerKey
        ? {etag: saved.etag, bearerKey}
        : {etag: saved.etag, clear: true};
      if (preimage.endpoint !== next.endpoint) request.endpoint = next.endpoint;
      return this.functionService.patchBearerCredentialAsync(preimage.id, request);
    }));
  }

  private valuesEqual(left: unknown, right: unknown): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
  }

  /**
   * Returns the appropriate validators for the endpoint field based on the function type
   * @param type The function type
   * @returns An array of validators
   */
  private getEndpointValidators(type: string) {
    // Only require endpoint for Logic App type
    if (type === 'Logic App') {
      return [Validators.required, Validators.pattern('https://.+')];
    }
    // For other types (Products, UI, ProductCard, Email, Workflow), endpoint is optional but should be a valid URL if provided
    return [Validators.pattern('https://.+')];
  }

  private parseAssistantMediaHosts(value: unknown): string[] {
    if (typeof value !== 'string') return [];
    return value.split(/[\r\n,]+/)
      .map(host => host.trim().replace(/\.$/, '').toLowerCase())
      .filter(Boolean);
  }

  private isExactPublicDnsHost(host: string): boolean {
    if (host.length > 253 ||
        host === 'localhost' ||
        host.endsWith('.localhost') ||
        host.endsWith('.local') ||
        host.endsWith('.internal') ||
        host.endsWith('.lan') ||
        host.endsWith('.home') ||
        host.endsWith('.home.arpa') ||
        host.includes('://') ||
        host.includes('/') ||
        host.includes(':') ||
        host.includes('*')) {
      return false;
    }
    const labels = host.split('.');
    return labels.length >= 2 && labels.every(label =>
      label.length > 0 &&
      label.length <= 63 &&
      /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(label));
  }

  private updateTypeValidators(type: string): void {
    const endpoint = this.settingsForm.get('endpoint');
    endpoint?.setValidators(this.getEndpointValidators(type));
    endpoint?.updateValueAndValidity({emitEvent: false});

    const parameters = this.settingsForm.get('parameters');
    parameters?.setValidators(type === 'Email'
      ? []
      : [Validators.required, this.jsonValidator]);
    parameters?.updateValueAndValidity({emitEvent: false});

    const recipient = this.settingsForm.get('emailRecipient');
    const subject = this.settingsForm.get('emailSubject');
    if (type === 'Email') {
      recipient?.setValidators([Validators.required, Validators.email]);
      subject?.setValidators([
        Validators.required,
        Validators.maxLength(200),
        Validators.pattern(/^(?!.*\{\{)[^\r\n]+$/)
      ]);
    } else {
      recipient?.clearValidators();
      subject?.clearValidators();
    }
    recipient?.updateValueAndValidity({emitEvent: false});
    subject?.updateValueAndValidity({emitEvent: false});

    const lookups = this.settingsForm.get('argumentLookupsJson');
    lookups?.setValidators(type === 'Logic App'
      ? [Validators.required, this.argumentLookupsValidator]
      : []);
    lookups?.updateValueAndValidity({emitEvent: false});
  }

  private updateFormSubmissionValidators(): void {
    const enabled = this.settingsForm.get('type')?.value === 'UI' &&
      this.settingsForm.get('formSubmissionEnabled')?.value === true;
    const requiredControls = [
      'formSubmissionToolName'
    ];
    requiredControls.forEach(name => {
      const control = this.settingsForm.get(name);
      if (enabled) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity({emitEvent: false});
    });

    const functionId = this.settingsForm.get('formSubmissionFunctionId');
    if (enabled) {
      functionId?.setValidators([
        Validators.required,
        control => this.formSubmissionTargets.some(target => target.id === control.value)
          ? null
          : {targetUnavailable: true}
      ]);
    } else {
      functionId?.clearValidators();
    }
    functionId?.updateValueAndValidity({emitEvent: false});

    const version = this.settingsForm.get('formSubmissionFunctionVersion');
    if (enabled) {
      version?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      version?.clearValidators();
    }
    version?.updateValueAndValidity({emitEvent: false});

    const inputSchema = this.settingsForm.get('formSubmissionInputSchema');
    if (enabled) {
      inputSchema?.setValidators([Validators.required, this.closedSchemaValidator]);
    } else {
      inputSchema?.clearValidators();
    }
    inputSchema?.updateValueAndValidity({emitEvent: false});

    const targetContract = this.settingsForm.get('formSubmissionTargetContractVersion');
    if (enabled) {
      targetContract?.setValue('configured-function.v1', {emitEvent: false});
      targetContract?.setValidators([
        Validators.required,
        Validators.pattern(/^configured-function\.v1$/)
      ]);
    } else {
      targetContract?.setValidators(Validators.pattern(/^configured-function\.v1$/));
    }
    targetContract?.updateValueAndValidity({emitEvent: false});

    const presentationValidators: Record<string, ValidatorFn[]> = {
      hubSpotFormRegion: [Validators.required, Validators.pattern(/^(eu1|na1)$/)],
      hubSpotFormPortalId: [Validators.required, Validators.pattern(/^[1-9][0-9]{0,19}$/)],
      hubSpotFormId: [Validators.required, Validators.pattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/)]
    };
    Object.entries(presentationValidators).forEach(([name, validators]) => {
      const control = this.settingsForm.get(name);
      if (enabled) control?.setValidators(validators);
      else control?.clearValidators();
      control?.updateValueAndValidity({emitEvent: false});
    });
  }

  isLegacyConfiguration(): boolean {
    return this.functionOption !== null && this.functionOption.contractVersion !== 'configured-function.v1';
  }

  private updateContractValidators(isGoverned: boolean): void {
    const outputSchema = this.settingsForm.get('outputSchema');
    const authority = this.settingsForm.get('authority');
    if (isGoverned) {
      outputSchema?.setValidators([Validators.required, this.closedSchemaValidator]);
      authority?.setValidators([Validators.required]);
    } else {
      outputSchema?.clearValidators();
      authority?.clearValidators();
    }
    outputSchema?.updateValueAndValidity({emitEvent: false});
    authority?.updateValueAndValidity({emitEvent: false});
    const requestMethod = this.settingsForm.get('requestMethod');
    const requestPlacement = this.settingsForm.get('requestArgumentPlacement');
    const requiresGovernedRequest = isGoverned && this.settingsForm.get('type')?.value === 'Logic App';
    requestMethod?.setValidators(requiresGovernedRequest
      ? [Validators.required, Validators.pattern(/^POST$/)]
      : []);
    requestPlacement?.setValidators(requiresGovernedRequest
      ? [Validators.required, Validators.pattern(/^json_body$/)]
      : []);
    requestMethod?.updateValueAndValidity({emitEvent: false});
    requestPlacement?.updateValueAndValidity({emitEvent: false});
    this.updateIdempotencyValidators(isGoverned);
  }

  private readonly argumentLookupsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (typeof control.value !== 'string') return {invalidArgumentLookups: true};

    let lookups: unknown;
    try {
      lookups = JSON.parse(control.value);
    } catch {
      return {invalidArgumentLookups: true};
    }
    if (!Array.isArray(lookups)) return {invalidArgumentLookups: true};

    const allowedFields = new Set(['sourceArgument', 'targetArgument', 'values', 'removeSourceArgument',
      'required', 'scopeArgument', 'scopedValues']);
    for (const candidate of lookups) {
      if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        return {invalidArgumentLookups: true};
      }
      const lookup = candidate as Record<string, unknown>;
      if (Object.keys(lookup).some(field => !allowedFields.has(field)) ||
          typeof lookup['sourceArgument'] !== 'string' ||
          typeof lookup['targetArgument'] !== 'string' ||
          typeof lookup['removeSourceArgument'] !== 'boolean' ||
          (lookup['required'] !== undefined && typeof lookup['required'] !== 'boolean') ||
          (lookup['scopeArgument'] !== undefined && typeof lookup['scopeArgument'] !== 'string') ||
          !lookup['values'] || typeof lookup['values'] !== 'object' || Array.isArray(lookup['values'])) {
        return {invalidArgumentLookups: true};
      }

      const source = this.normalizeLookupValue(lookup['sourceArgument']);
      const target = this.normalizeLookupValue(lookup['targetArgument']);
      if (!source || !target) return {emptyArgumentLookup: true};

      const scope = typeof lookup['scopeArgument'] === 'string' ? lookup['scopeArgument'].trim() : '';
      const scopedValues = lookup['scopedValues'] ?? {};
      if (!scopedValues || typeof scopedValues !== 'object' || Array.isArray(scopedValues)) {
        return {invalidArgumentLookups: true};
      }
      const maps = Object.entries(scopedValues as Record<string, unknown>);
      if (scope ? Object.keys(lookup['values']).length > 0 || maps.length === 0 : maps.length > 0) {
        return {invalidArgumentLookups: true};
      }
      const scopes = new Set<string>();
      const mappings: [string, unknown][] = scope ? maps : [['', lookup['values']]];
      for (const [rawScope, values] of mappings) {
        const normalizedScope = this.normalizeLookupValue(rawScope);
        if (scope && !normalizedScope) return {emptyArgumentLookup: true};
        if (scopes.has(normalizedScope)) return {ambiguousArgumentLookup: true};
        scopes.add(normalizedScope);
        if (!values || typeof values !== 'object' || Array.isArray(values)) return {invalidArgumentLookups: true};
        const error = this.validateLookupValues(values as Record<string, unknown>);
        if (error) return error;
      }
    }
    return null;
  };

  private validateLookupValues(mapping: Record<string, unknown>): ValidationErrors | null {
    const values = Object.entries(mapping);
    if (values.length === 0) return {emptyArgumentLookup: true};
    const normalizedMappings = new Map<string, string>();
    for (const [rawKey, rawValue] of values) {
      if (typeof rawValue !== 'string' || !rawValue.trim()) return {emptyArgumentLookup: true};
      const normalizedKey = this.normalizeLookupValue(rawKey);
      if (!normalizedKey) return {emptyArgumentLookup: true};
      const existingValue = normalizedMappings.get(normalizedKey);
      if (existingValue !== undefined && existingValue !== rawValue) {
        return {ambiguousArgumentLookup: true};
      }
      normalizedMappings.set(normalizedKey, rawValue);
    }
    return null;
  }

  private parseArgumentLookups(value: string): IFunctionArgumentLookup[] {
    return JSON.parse(value) as IFunctionArgumentLookup[];
  }

  private normalizeLookupValue(value: string): string {
    return value.normalize('NFKC').trim().toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  }

  private updateIdempotencyValidators(isGoverned: boolean): void {
    const mode = this.settingsForm.get('idempotencyMode');
    const header = this.settingsForm.get('idempotencyHeaderName');
    const requiresProviderCallId = isGoverned && this.settingsForm.get('type')?.value === 'Logic App';

    mode?.setValidators(requiresProviderCallId
      ? [Validators.required, Validators.pattern(/^provider_call_id$/)]
      : [Validators.required]);
    const providerCallIdSelected = mode?.value === 'provider_call_id';
    header?.setValidators(providerCallIdSelected
      ? [Validators.required, this.idempotencyHeaderValidator]
      : []);
    mode?.updateValueAndValidity({emitEvent: false});
    header?.updateValueAndValidity({emitEvent: false});
  }

  private readonly idempotencyHeaderValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = typeof control.value === 'string' ? control.value.trim() : '';
    if (!/^[A-Za-z0-9-]{1,64}$/.test(value)) return {invalidIdempotencyHeader: true};
    return ['authorization', 'cookie', 'host', 'content-length', 'client'].includes(value.toLowerCase())
      ? {invalidIdempotencyHeader: true}
      : null;
  };

  delete() {
    if (!this.functionOption) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Are you sure you want to delete this function?',
        message: 'This will delete the function record. Once deleted, it cannot be rolled back.',
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isDeleting = true;
        this.snackBar.showInfo('Deleting function...', 3000);
        if (!this.functionOption?.etag) {
          this.snackBar.showError('Reload the function before deleting it.', 3000);
          this.isDeleting = false;
          return;
        }
        this.functionService.deleteWithEtagAsync(this.functionOption.id, this.functionOption.etag).subscribe(() => {
          this.snackBar.showSuccess('Function deleted successfully.', 3000);
          this.router.navigate(['/ai/functions']);
        }, error => {
          console.error('Error deleting function', error);
          this.snackBar.showError(error?.status === 412 ? 'Configuration changed elsewhere. Reload before deleting.' : 'Error deleting function.', 3000);
          this.isDeleting = false;
        });
      } else {
        this.snackBar.showInfo('Delete action canceled.', 3000);
      }
    });
  }

  updateWorkflowBinding(binding: IWorkflowFunctionBinding | null): void {
    this.workflowBinding = binding;
    if (this.functionOption) {
      this.functionOption.workflowBinding = binding;
    }
    this.settingsForm.markAsDirty();
    this.settingsForm.markAsTouched();
    if (binding) {
      this.settingsForm.patchValue({parameters: binding.inputSchema});
    }
  }

  selectFormSubmissionTarget(functionId: string): void {
    const target = this.formSubmissionTargets.find(candidate => candidate.id === functionId);
    if (!target) {
      this.settingsForm.patchValue({
        formSubmissionFunctionVersion: 1,
        formSubmissionToolName: '',
        formSubmissionTargetContractVersion: 'configured-function.v1',
        formSubmissionInputSchema: ''
      });
      return;
    }
    this.settingsForm.patchValue({
      formSubmissionFunctionVersion: target.version,
      formSubmissionToolName: target.toolName,
      formSubmissionTargetContractVersion: target.contractVersion,
      formSubmissionInputSchema: target.parameters
    });
    this.settingsForm.markAsDirty();
    this.settingsForm.markAsTouched();
  }

  private async loadFormSubmissionTargets(): Promise<void> {
    try {
      const functions = await this.functionService.getAllAsync().toPromise();
      this.formSubmissionTargets = (functions ?? [])
        .filter(candidate => candidate.enabled &&
          candidate.type === 'Logic App' &&
          candidate.contractVersion === 'configured-function.v1' &&
          candidate.idempotency?.mode === 'provider_call_id' &&
          typeof candidate.toolName === 'string' && candidate.toolName.length > 0 &&
          Number(candidate.version) >= 1)
        .sort((left, right) => left.name.localeCompare(right.name));
      this.settingsForm.get('formSubmissionFunctionId')?.updateValueAndValidity({emitEvent: false});
    } catch {
      this.formSubmissionTargets = [];
      this.settingsForm.get('formSubmissionFunctionId')?.updateValueAndValidity({emitEvent: false});
      this.snackBar.showError('Configured form targets are unavailable. Reload before editing this binding.', 5000);
    }
  }

  disableSave(): boolean {
    return this.isSaving || !this.settingsForm.touched || (this.requiresWorkflowBinding() && !this.hasReadyWorkflowBinding());
  }

  private requiresWorkflowBinding(): boolean {
    return this.settingsForm.get('type')?.value === 'Workflow';
  }

  hasReadyWorkflowBinding(): boolean {
    return isWorkflowFunctionBindingReady(this.workflowBinding);
  }

  private workflowBindingForSave(): IWorkflowFunctionBinding | null {
    if (!this.workflowBinding) {
      return null;
    }

    const {availability: _availability, ...binding} = this.workflowBinding;
    return binding;
  }

  private formSubmissionBindingForSave(value: {
    enabled: boolean;
    functionId: string;
    functionVersion: number;
    toolName: string;
    contractVersion: string;
    inputSchema: string;
  }, stored: IFormSubmissionFunctionBinding | null | undefined): IFormSubmissionFunctionBinding | null | undefined {
    if (this.settingsForm.get('type')?.value !== 'UI') {
      return stored ? null : stored;
    }
    if (!value.enabled) {
      return stored === undefined ? undefined : null;
    }

    return {
      functionId: value.functionId.trim(),
      functionVersion: Number(value.functionVersion),
      toolName: value.toolName.trim(),
      functionType: 'Logic App',
      contractVersion: value.contractVersion.trim(),
      inputSchema: value.inputSchema.trim()
    };
  }

  private hubSpotFormPresentationForSave(value: {
    enabled: boolean;
    region: string;
    portalId: string;
    formId: string;
  }, stored: IHubSpotFormPresentation | null | undefined): IHubSpotFormPresentation | null | undefined {
    if (this.settingsForm.get('type')?.value !== 'UI') {
      return stored ? null : stored;
    }
    if (!value.enabled) {
      return stored === undefined ? undefined : null;
    }
    return {
      contractVersion: 'hubspot-form-presentation.v1',
      region: value.region as 'eu1' | 'na1',
      portalId: value.portalId.trim(),
      formId: value.formId.trim()
    };
  }
}
